/**
 * Utility functions to parse LaTeX compilation warnings and errors
 * Extracts line numbers and page numbers for navigation
 */

export interface ParsedWarning {
  lineNumber: number | null
  pageNumber: number | null
  message: string
  type: 'warning' | 'error'
}

/**
 * Parse a warning or error message to extract line number and page number
 * 
 * Examples:
 * - "LaTeX Warning: Reference `chap:mechanism' on page 81 undefined on input line 2665."
 * - "Package hyperref Warning: Difference (2) between bookmark levels is greater than one, level fixed on input line 103."
 * - "LaTeX Error: Missing \begin{document}."
 * 
 * @param message The warning or error message
 * @param type Whether this is a warning or error
 * @returns Parsed warning with line number, page number, and message
 */
export function parseLatexWarning(message: string, type: 'warning' | 'error'): ParsedWarning {
  let lineNumber: number | null = null
  let pageNumber: number | null = null
  
  // Extract line number: "on input line 2665" or "at line 103"
  const lineMatch = message.match(/\bon input line (\d+)\b/i) || message.match(/\bat line (\d+)\b/i)
  if (lineMatch) {
    lineNumber = parseInt(lineMatch[1], 10)
  }
  
  // Extract page number: "on page 81" or "on page ii"
  const pageMatch = message.match(/\bon page (\d+)\b/i)
  if (pageMatch) {
    pageNumber = parseInt(pageMatch[1], 10)
  }
  
  // Handle roman numeral page numbers (like "on page ii")
  const romanPageMatch = message.match(/\bon page ([ivxlcdm]+)\b/i)
  if (romanPageMatch && !pageNumber) {
    // Convert roman numeral to number (simple conversion for common cases)
    const roman = romanPageMatch[1].toLowerCase()
    const romanMap: Record<string, number> = {
      'i': 1, 'ii': 2, 'iii': 3, 'iv': 4, 'v': 5,
      'vi': 6, 'vii': 7, 'viii': 8, 'ix': 9, 'x': 10
    }
    if (romanMap[roman]) {
      pageNumber = romanMap[roman]
    }
  }
  
  return {
    lineNumber,
    pageNumber,
    message,
    type
  }
}
